import {BynderAsset} from '@/services/bynderAssetService'
import {Button} from 'pepsico-ds'
import {useRef, useState} from 'react'
import {ImageGalleryModal} from './ImageGalleryModal'
import './ImagePicker.scss'

interface ImagePickerProps {
  accept?: string
  classNameProp?: string
  shortDescription?: string
  description?: string
  disabled?: boolean
  hasError?: boolean
  maxSize?: number
  onUpdate: (file: File | string, fileName?: string, assetId?: string) => void
  enableGallery?: boolean
  galleryTag?: string
}

import {DEFAULT_ACCEPTED_IMAGE_FORMATS} from './constants'

export const ImagePicker = ({
  accept = DEFAULT_ACCEPTED_IMAGE_FORMATS,
  classNameProp,
  shortDescription,
  description = 'Select an image',
  disabled = false,
  hasError = false,
  onUpdate,
  enableGallery = false,
  galleryTag,
}: ImagePickerProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      onUpdate(file, file.name)
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()

    const file = event.dataTransfer.files?.[0]
    if (file) {
      onUpdate(file, file.name)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleOpenGallery = () => {
    setIsGalleryOpen(true)
  }

  const handleCloseGallery = () => {
    setIsGalleryOpen(false)
  }

  const handleSelectImage = (asset: BynderAsset) => {
    const selectedUrl =
      asset.original ||
      asset.transformBaseUrl ||
      asset.thumbnails?.transformBaseUrl ||
      asset.thumbnails?.WEBP_Original ||
      asset.thumbnails?.webimage
    if (!selectedUrl) {
      return
    }

    onUpdate(selectedUrl, asset.name, asset.id)
    setIsGalleryOpen(false)
  }

  return (
    <>
      <div
        className={`image-uploader ${classNameProp || ''} ${hasError ? 'image-uploader--error' : ''}`}
        data-testid="image-uploader"
      >
        <div className="image-picker-upload">
          <div
            className="image-picker-drop-zone image-picker-container gap-2"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {shortDescription && <div className="font-xs">{shortDescription}</div>}
            <div className="upload-description">{description}</div>
            <div className="image-picker-buttons">
              {enableGallery && (
                <Button
                  data-testid="btn-gallery"
                  size="small"
                  variant="secondary"
                  onClick={handleOpenGallery}
                  iconTrailing="image"
                  disabled={disabled}
                >
                  Select from gallery
                </Button>
              )}
              <Button
                data-testid="btn-select-file"
                size="small"
                variant="secondary"
                onClick={handleButtonClick}
                iconTrailing="upload"
                disabled={disabled}
              >
                Upload image
              </Button>
            </div>
            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              style={{display: 'none'}}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {enableGallery && (
        <ImageGalleryModal
          isOpen={isGalleryOpen}
          onClose={handleCloseGallery}
          onSelectImage={handleSelectImage}
          tag={galleryTag}
        />
      )}
    </>
  )
}
